import { isDevelopment } from "../../common/helpers/env";
/// <reference types="../window" />
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { GoogleOAuthProvider } from '@react-oauth/google';

import routingStartsWith from "../../common/helpers/routingStartsWith";

import theme from "../style/theme";
import getUser from "../store/user/actions/getUser";
import ErrorBoundary from "../components/ErrorBoundary";
import routing from "../routing";
import { IStateTree } from "../store/interfaces";
import GlobalCss from "../style";

import Footer from "../components/Footer";
import Cookie from "../components/Cookie";
import Nav from "../components/Nav";
import ScrollToTop from "../components/ScrollToTop";
import { hasItem } from "../cookie";

import Notifications from "./Notifications";
import NewVersionAvailable from "./NewVersionAvailable";
import * as Sentry from "@sentry/react";
import { darkColors } from "../style/colors";
import { resolveDarkMode, saveDarkMode } from "./store";
import { setDarkMode } from "../store/darkMode/actions";
import { googleClientId } from "../../common/parameters";
import Question from "../components/Question";

const App = () => {
    const [loaded, setLoaded] = useState(false);
    const user = useSelector((state: IStateTree) => state.user);
    // From the store, so that the server - which read the cookie - and the browser start
    // the first render in the same theme.
    const darkMode = useSelector((state: IStateTree) => state.darkMode);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const showCookie = !hasItem("cookie-accepted");
    const handleSetDarkMode = () => {
        saveDarkMode(!darkMode);
        dispatch(setDarkMode(!darkMode));
    }
    useEffect(() => {
        // The store holds what the server read out of the cookie - except in development,
        // where it renders no state at all - so the browser has the last word, and only
        // after hydration: the first render has to match the markup it is hydrating. The
        // cookie is written back so the next page arrives in the right theme to begin with.
        const resolved = resolveDarkMode();
        saveDarkMode(resolved);
        if (resolved !== darkMode) dispatch(setDarkMode(resolved));
    }, []);
    useEffect(() => {
        if (!user || !user.loggedIn) {
            if (isDevelopment) {
                dispatch(getUser());
            }
        } else {
            const { _id: id, email } = user;
            Sentry.setUser({
                id,
                email,
            });
            if (window.OneSignal) {
                window.OneSignal.push(function () {
                    window.OneSignal.showNativePrompt();
                });
            }
        }
        setLoaded(true);
    }, []);
    if (user === null) return null;
    let isPlaying = getIsPlaying(location.pathname, location.search);
    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div>
                <ThemeProvider theme={{
                    ...theme,
                    mode: darkMode ? "dark" : "light",
                    c: {
                        ...theme.c,
                        ...(darkMode ? darkColors : {})
                    }
                }}
                >
                    <>
                        <GlobalCss />
                        <ErrorBoundary>
                            {!isPlaying && <Nav darkMode={darkMode} setDarkMode={handleSetDarkMode} />}
                            <ScrollToTop><Outlet /></ScrollToTop>
                            {loaded && <NewVersionAvailable />}
                            {!isPlaying && <Footer />}
                            {!isPlaying && showCookie && <Cookie />}
                            <Notifications />
                            <Question />
                        </ErrorBoundary>
                    </>
                </ThemeProvider>
            </div>
        </GoogleOAuthProvider>
    );
}

export default App;

const getIsPlaying = (pathname: string, search: string) => {
    if (
        routingStartsWith(pathname, routing.share + "/") ||
        routingStartsWith(pathname, routing.play + "/") ||
        routingStartsWith(pathname, routing.publicPlay + "/") ||
        (routingStartsWith(pathname, routing.join) && pathname !== `/${routing.closedClassroom}`) ||
        (!routingStartsWith(pathname, routing.classes) &&
            !routingStartsWith(pathname, routing.homeworkCompleted) &&
            routingStartsWith(pathname, routing.homework) &&
            pathname !== "/" + routing.homework &&
            pathname !== "/" + routing.homework + "/" &&
            pathname.slice(0, routing.homeworkResult.length + 1) !== "/" + routing.homeworkResult) ||
        routingStartsWith(pathname, routing.battleOfTheHuns) ||
        routingStartsWith(pathname, routing.christmasEdition) ||
        search.includes("nativeApp=1")
    ) {
        return true;
    }
    return false;
};
